'use client'

import React from 'react'
import { User } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/utils/supabase/client'
import { Organization, Profile } from '../types'

type AuthContextType = {
  user: User | null
  profile: Profile | null
  organizations: Organization[]
  currentOrganization: Organization | null
  loading: boolean
  refreshOrganizations: (userId: string) => Promise<void>
  switchOrganization: (orgId: string) => void
  hasOrganization: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  organizations: [],
  currentOrganization: null,
  loading: true,
  refreshOrganizations: async () => {},
  switchOrganization: () => {},
  hasOrganization: false,
  signOut: async () => {},
})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const refreshOrganizations = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('owner_id', userId)

      if (error) {
        throw error
      }

      setOrganizations(data || [])

      if (!currentOrganization && data && data.length > 0) {
        setCurrentOrganization(data[0])
      }
    } catch (error) {
      console.error('Failed to refresh organizations:', error)
    }
  }

  const switchOrganization = (orgId: string) => {
    const newOrg = organizations.find((org: Organization) => org.id === orgId)
    if (newOrg) {
      setCurrentOrganization(newOrg)
    } else {
      console.warn(`Organization with ID ${orgId} not found.`)
    }
  }

  const signOut = async () => {
    try {
      await supabase.auth.signOut()
      setUser(null)
      setProfile(null)
      setOrganizations([])
      setCurrentOrganization(null)
      navigate('/')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(session.user)
        fetchProfile(session.user.id)
        refreshOrganizations(session.user.id)
      } else {
        setUser(null)
        setProfile(null)
        setOrganizations([])
        setCurrentOrganization(null)
      }
      setLoading(false)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        throw error
      }

      if (data) {
        setProfile(data)
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error)
    }
  }

  const hasOrganization = organizations.length > 0

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        organizations,
        currentOrganization,
        loading,
        refreshOrganizations,
        switchOrganization,
        hasOrganization,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
} 